import { NotificationItem } from '@/components/notifications/NotificationItem';
import { NotificationTabs } from '@/components/notifications/NotificationTabs';
import { getNotificationHref } from '@/components/notifications/notification-presentation';
import {
  useDismissNotificationAction,
  useMarkAllNotificationsReadAction,
  useMarkNotificationReadAction,
} from '@/hooks/actions/use-notification-actions';
import {
  useNotificationsInfiniteQuery,
  useNotificationUnreadCountQuery,
} from '@/hooks/queries/use-notifications-query';
import {
  createNotificationDateGrouper,
  NOTIFICATION_DATE_GROUP_ORDER,
  type NotificationDateGroup,
} from '@/lib/notification-date-group';
import type { NotificationDto, NotificationTab } from '@fluenceur/types';
import { NotificationTab as NotificationTabValue } from '@fluenceur/types';
import { ThemedButton } from '@fluenceur/ui';
import { Box, Button, Circle, Flex, Spinner, Text, VStack } from '@chakra-ui/react';
import { BellOff } from 'iconoir-react';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type NotificationsPanelProps = {
  onNavigate?: () => void;
};

const NOTIFICATION_LIMIT = 20;

type NotificationGroup = {
  key: NotificationDateGroup;
  items: NotificationDto[];
};

function groupByDate(notifications: NotificationDto[]): NotificationGroup[] {
  // One reference instant for the whole pass, so a batch cannot straddle
  // midnight and split two adjacent notifications into different sections.
  const resolveGroup = createNotificationDateGrouper();
  // Pre-seeded in display order, so iterating the map yields the sections in order.
  const buckets = new Map<NotificationDateGroup, NotificationDto[]>(
    NOTIFICATION_DATE_GROUP_ORDER.map((key) => [key, []])
  );

  for (const notification of notifications) {
    buckets.get(resolveGroup(notification.occurredAt))?.push(notification);
  }

  return [...buckets]
    .filter(([, items]) => items.length > 0)
    .map(([key, items]) => ({ key, items }));
}

export function NotificationsPanel({ onNavigate }: NotificationsPanelProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<NotificationTab>(NotificationTabValue.ALL);
  const notifications = useNotificationsInfiniteQuery({ tab, limit: NOTIFICATION_LIMIT });
  const unreadCount = useNotificationUnreadCountQuery();
  const { isPending: isMarkAllReadPending, mutate: markAllRead } =
    useMarkAllNotificationsReadAction();
  // The pending flags are scoped to the row actually mutating. A global flag
  // would change props on every row, defeating NotificationItem's memo and
  // disabling every other row's controls while one request is in flight.
  const {
    isPending: isDismissPending,
    mutate: dismissNotification,
    variables: dismissingId,
  } = useDismissNotificationAction();
  const {
    isPending: isMarkReadPending,
    mutateAsync: markNotificationRead,
    variables: openingId,
  } = useMarkNotificationReadAction();
  const navigate = useNavigate();
  const refetchNotifications = notifications.refetch;
  const refetchUnreadCount = unreadCount.refetch;

  const unreadForCurrentTab =
    tab === NotificationTabValue.AGENTS
      ? (unreadCount.data?.agents ?? 0)
      : (unreadCount.data?.total ?? 0);
  const items = useMemo(
    () => notifications.data?.pages.flatMap((page) => page.notifications) ?? [],
    [notifications.data?.pages]
  );
  const groups = useMemo(() => groupByDate(items), [items]);
  const isInitialLoading = notifications.isLoading && items.length === 0;
  const isError = notifications.isError || unreadCount.isError;

  const retry = useCallback(() => {
    void refetchNotifications();
    void refetchUnreadCount();
  }, [refetchNotifications, refetchUnreadCount]);

  const handleTabChange = useCallback((event: { value: string | null }) => {
    if (event.value === NotificationTabValue.ALL || event.value === NotificationTabValue.AGENTS) {
      setTab(event.value);
    }
  }, []);

  const handleNotificationDismiss = useCallback(
    (notificationId: string) => {
      dismissNotification(notificationId);
    },
    [dismissNotification]
  );

  const handleNotificationOpen = useCallback(
    (notification: NotificationDto) => {
      void (async () => {
        try {
          if (!notification.readAt) {
            await markNotificationRead(notification.id);
          }
        } catch {
          return;
        }

        const href = getNotificationHref(notification);
        if (href) {
          navigate(href);
          onNavigate?.();
        }
      })();
    },
    [markNotificationRead, navigate, onNavigate]
  );

  return (
    <Flex direction="column" bg="panelBg" h="full" maxH="100%" overflow="hidden">
      <Flex px="16px" pt="16px" pb="12px" align="center" justify="space-between" gap={3}>
        <Text
          fontFamily="heading"
          fontSize="16px"
          fontWeight="semibold"
          letterSpacing="-0.01em"
          color="ink"
          lineHeight="1.2"
        >
          {t('notifications.title')}
        </Text>

        <Button
          variant="plain"
          h="auto"
          minW={0}
          p={0}
          fontFamily="body"
          fontSize="13px"
          fontWeight="medium"
          color="inkMuted"
          flexShrink={0}
          disabled={unreadForCurrentTab <= 0 || isMarkAllReadPending}
          loading={isMarkAllReadPending}
          onClick={() => markAllRead(tab)}
          _hover={{ color: 'ink', textDecoration: 'underline', textUnderlineOffset: '3px' }}
          _disabled={{ opacity: 0.5, cursor: 'not-allowed', textDecoration: 'none' }}
        >
          {t('notifications.markAllRead')}
        </Button>
      </Flex>

      <Box px="16px" pb="12px" borderBottom="1px solid" borderColor="border.subtle">
        <NotificationTabs
          tab={tab}
          onTabChange={handleTabChange}
          totalCount={unreadCount.data?.total ?? 0}
          agentsCount={unreadCount.data?.agents ?? 0}
        />
      </Box>

      {/* The cap belongs on the scrolling list, not the panel: on the panel it
          would also absorb the header and tabs and cut the list short. */}
      <Box flex="1" minH={0} overflowY="auto" maxH={{ base: 'none', md: '560px' }}>
        {isInitialLoading && (
          <VStack py={12} color="inkMuted" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="12.5px">{t('notifications.loading')}</Text>
          </VStack>
        )}

        {!isInitialLoading && isError && (
          <VStack py={12} px={6} textAlign="center" gap={3}>
            <Box>
              <Text fontFamily="heading" fontSize="13.5px" fontWeight="semibold" color="ink">
                {t('notifications.error.title')}
              </Text>
              <Text mt={1} fontSize="13px" color="inkMuted">
                {t('notifications.error.description')}
              </Text>
            </Box>
            <ThemedButton variant="secondary"
            size="chip" onClick={retry}>
              {t('notifications.error.retry')}
            </ThemedButton>
          </VStack>
        )}

        {!isInitialLoading && !isError && items.length === 0 && (
          <VStack py="48px" px={6} textAlign="center" gap={0}>
            <Circle size="44px" bg="softCream" color="inkMuted" mb="12px">
              <BellOff width={20} height={20} aria-hidden="true" />
            </Circle>
            <Text fontFamily="heading" fontSize="14px" fontWeight="semibold" color="ink">
              {t('notifications.empty.title')}
            </Text>
            <Text mt={1} fontSize="13px" lineHeight="1.45" color="inkMuted">
              {t('notifications.empty.description')}
            </Text>
          </VStack>
        )}

        {!isInitialLoading &&
          !isError &&
          groups.map((group) => (
            <Fragment key={group.key}>
              <Text
                px="16px"
                pt="14px"
                pb="6px"
                fontFamily="heading"
                fontSize="11.5px"
                fontWeight="semibold"
                letterSpacing="0.06em"
                textTransform="uppercase"
                color="inkMuted"
              >
                {t(`notifications.groups.${group.key}`)}
              </Text>

              {group.items.map((notification, index) => (
                <NotificationItem
                  key={notification.id}
                  isDismissPending={isDismissPending && dismissingId === notification.id}
                  isLast={index === group.items.length - 1}
                  isMarkReadPending={isMarkReadPending && openingId === notification.id}
                  notification={notification}
                  onDismiss={handleNotificationDismiss}
                  onOpen={handleNotificationOpen}
                />
              ))}
            </Fragment>
          ))}

        {!isInitialLoading && !isError && notifications.hasNextPage && (
          <Box px="16px" py={4}>
            <ThemedButton
              variant="secondary"
            size="chip"
              w="full"
              loading={notifications.isFetchingNextPage}
              onClick={() => {
                void notifications.fetchNextPage();
              }}
            >
              {t('notifications.loadMore')}
            </ThemedButton>
          </Box>
        )}
      </Box>
    </Flex>
  );
}
